// netActivity — V3 słaby zasięg B-1 (B3/B4): wspólny stan sieci dla UI i kolejki.
//
//   - licznik GET-ów UI w locie (B4: kolejka NIE startuje kolejnego uploadu, gdy UI
//     właśnie czeka na dane; uplink 5G przy 200 kB/s to wspólne łóżko),
//   - sygnał „słaby zasięg" (B3: GET przekroczył limit, pokazujemy ostatni stan z cache;
//     baner „Słaby zasięg, pokazuję ostatni stan" zamiast „Offline"),
//   - stałe limitów (DN1, T Martina 12.09): GET z UI 6 s, upload 60 s per plik.
//
// Bez Reacta i bez IDB — czyste zdarzenia, testowalne w node:test.

export const UI_GET_TIMEOUT_MS = 6_000;
export const UPLOAD_TIMEOUT_MS = 60_000;
export const SILENT_RETRY_TIMEOUT_MS = 30_000;
/** Po tylu ms bez kolejnego timeoutu sygnał „słaby zasięg" gaśnie sam. */
export const WEAK_SIGNAL_TTL_MS = 60_000;

let inFlightGets = 0;
let weakSince = null;
let weakPath = null;
const listeners = new Set();

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function emit(ev) {
  for (const fn of listeners) {
    try { fn(ev); } catch { /* nic */ }
  }
}

export function beginUiGet() { inFlightGets += 1; }
export function endUiGet() { inFlightGets = Math.max(0, inFlightGets - 1); }
export function getInFlightGets() { return inFlightGets; }

/**
 * B4: czeka aż UI nie ma GET-ów w locie (max `maxMs`, domyślnie tyle, ile trwa timeout
 * GET-u — dłużej i tak żaden GET nie wisi). true = cisza, false = limit czasu.
 */
export async function waitForUiIdle(maxMs = UI_GET_TIMEOUT_MS, stepMs = 100) {
  const t0 = Date.now();
  while (inFlightGets > 0 && Date.now() - t0 < maxMs) await sleep(stepMs);
  return inFlightGets === 0;
}

/** GET przekroczył limit, pokazano cache → baner „Słaby zasięg". */
export function markWeakSignal(path) {
  weakSince = Date.now();
  weakPath = path || null;
  emit({ type: 'weak', path: weakPath, since: weakSince });
}

/** Udane żądanie → sygnał gaśnie (jeśli był). */
export function markSignalOk(path) {
  if (weakSince == null) return;
  weakSince = null;
  weakPath = null;
  emit({ type: 'ok', path: path || null });
}

/** Stan do banera/Serwisu. `weak` wygasa samo po WEAK_SIGNAL_TTL_MS. */
export function getNetState() {
  const weak = weakSince != null && Date.now() - weakSince < WEAK_SIGNAL_TTL_MS;
  return { weak, weakSince: weak ? weakSince : null, weakPath: weak ? weakPath : null, inFlightGets };
}

export function subscribeNetState(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Typ sieci do diagnostyki (B6) — Network Information API, gdy dostępne (brak na iOS). */
export function readNetworkInfo() {
  try {
    const c = typeof navigator !== 'undefined' ? (navigator.connection || navigator.mozConnection || navigator.webkitConnection) : null;
    if (!c) return { effectiveType: null, downlink: null, rtt: null, saveData: null };
    return {
      effectiveType: c.effectiveType ?? null,
      downlink: c.downlink ?? null,
      rtt: c.rtt ?? null,
      saveData: c.saveData ?? null,
    };
  } catch {
    return { effectiveType: null, downlink: null, rtt: null, saveData: null };
  }
}

/** Test hook. */
export function __resetNetActivityForTest() {
  inFlightGets = 0;
  weakSince = null;
  weakPath = null;
  listeners.clear();
}
